/**
 * Visor de adjuntos a pantalla completa, sobre la ficha.
 *
 * Barra arriba (ícono, nombre, datos, «3 de 7», Descargar y Cerrar), flechas a los lados y ←/→ para
 * pasar entre adjuntos (imágenes primero, luego documentos), deshabilitadas en los extremos.
 * Escena según `tipoDePrevisualizacion`: imagen, texto (con tope, siempre como texto, nunca HTML) o
 * un panel para descargar lo que el navegador no muestra.
 *
 * Esc cierra solo el visor: se escucha en captura y se marca con `preventDefault`, que la ficha
 * (`PanelTarea`) respeta. El foco entra al visor al abrir, se queda dentro con Tab y vuelve a quien lo
 * abrió al cerrar. Se identifica el adjunto por id: el refresco de 30 s no lo cierra.
 */
import { useEffect, useRef, type KeyboardEvent as EventoTeclado } from 'react';
import type { Adjunto } from '@cdtec-backlog/contrato';
import { Icono } from '../../componentes/Icono';
import { urlAdjunto, mensajeDeErrorAdjunto } from './api';
import { useTextoAdjunto } from './hooks';
import { IconoExtension } from './IconoExtension';
import { MetaAdjunto } from './MetaAdjunto';
import { MAX_BYTES_TEXTO, tipoDePrevisualizacion } from './previsualizacion';
import './VisorAdjuntos.css';

interface Props {
  /** Todos los adjuntos, ya en el orden del visor. */
  adjuntos: Adjunto[];
  actualId: number;
  onCambiar: (id: number) => void;
  onCerrar: () => void;
}

const FOCALIZABLES = 'a[href], button:not(:disabled), [tabindex]:not([tabindex="-1"])';

export function VisorAdjuntos({ adjuntos, actualId, onCambiar, onCerrar }: Props) {
  const raiz = useRef<HTMLDivElement>(null);
  const posicion = adjuntos.findIndex((a) => a.id === actualId);
  const actual = adjuntos[posicion];
  const anterior = posicion > 0 ? adjuntos[posicion - 1] : undefined;
  const siguiente = posicion >= 0 ? adjuntos[posicion + 1] : undefined;

  // Las funciones llegan nuevas en cada render (también con el refresco): se guardan en una ref para que
  // los efectos corran una sola vez y no pierdan el foco que deben devolver.
  const acciones = useRef({ onCerrar, onCambiar, anterior, siguiente });
  useEffect(() => {
    acciones.current = { onCerrar, onCambiar, anterior, siguiente };
  });

  // Si el adjunto abierto desaparece (lo quitó alguien), el visor se cierra.
  useEffect(() => {
    if (!actual) acciones.current.onCerrar();
  }, [actual]);

  useEffect(() => {
    const previo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    raiz.current?.focus();
    const alTeclear = (evento: KeyboardEvent) => {
      const {
        onCerrar: cerrar,
        onCambiar: cambiar,
        anterior: ant,
        siguiente: sig,
      } = acciones.current;
      if (evento.key === 'Escape') {
        // En captura + preventDefault: la ficha ignora los Escape ya atendidos y sigue abierta.
        evento.preventDefault();
        cerrar();
      } else if (evento.key === 'ArrowLeft' && ant) {
        evento.preventDefault();
        cambiar(ant.id);
      } else if (evento.key === 'ArrowRight' && sig) {
        evento.preventDefault();
        cambiar(sig.id);
      }
    };
    window.addEventListener('keydown', alTeclear, true);
    return () => {
      window.removeEventListener('keydown', alTeclear, true);
      if (previo?.isConnected) previo.focus();
    };
  }, []);

  // Tab da la vuelta dentro del visor (y no llega a la trampa de foco de la ficha).
  const atrapar = (evento: EventoTeclado<HTMLDivElement>) => {
    if (evento.key !== 'Tab' || !raiz.current) return;
    evento.stopPropagation();
    const lista = Array.from(raiz.current.querySelectorAll<HTMLElement>(FOCALIZABLES));
    const primero = lista[0];
    const ultimo = lista[lista.length - 1];
    if (!primero || !ultimo) return;
    const activo = document.activeElement;
    if (evento.shiftKey && (activo === primero || activo === raiz.current)) {
      evento.preventDefault();
      ultimo.focus();
    } else if (!evento.shiftKey && activo === ultimo) {
      evento.preventDefault();
      primero.focus();
    }
  };

  if (!actual) return null;

  return (
    <div
      ref={raiz}
      className="visor"
      role="dialog"
      aria-modal="true"
      aria-label={`Vista previa de ${actual.nombre}`}
      tabIndex={-1}
      onKeyDown={atrapar}
      // Que el clic no llegue al velo de la ficha, que cierra al hacer clic fuera.
      onMouseDown={(evento) => evento.stopPropagation()}
    >
      <div className="visor__barra">
        <IconoExtension adjunto={actual} />
        <div className="visor__info">
          <span className="visor__nombre" title={actual.nombre}>
            {actual.nombre}
          </span>
          <MetaAdjunto adjunto={actual} className="visor__meta" />
        </div>
        <span className="visor__contador" aria-live="polite">
          {posicion + 1} de {adjuntos.length}
        </span>
        <a
          className="visor__boton"
          href={urlAdjunto(actual.id)}
          download={actual.nombre}
          aria-label={`Descargar ${actual.nombre}`}
          title="Descargar"
        >
          <Icono nombre="bajar" tamano={18} />
        </a>
        <button
          type="button"
          className="visor__boton"
          aria-label="Cerrar vista previa"
          title="Cerrar (Esc)"
          onClick={onCerrar}
        >
          <Icono nombre="x" tamano={18} />
        </button>
      </div>
      <div className="visor__cuerpo">
        <button
          type="button"
          className="visor__nav visor__nav--anterior"
          aria-label="Anterior"
          title="Anterior (←)"
          disabled={!anterior}
          onClick={() => anterior && onCambiar(anterior.id)}
        >
          <Icono nombre="izq" tamano={20} />
        </button>
        <div className="visor__escena">
          <Escena key={actual.id} adjunto={actual} />
        </div>
        <button
          type="button"
          className="visor__nav visor__nav--siguiente"
          aria-label="Siguiente"
          title="Siguiente (→)"
          disabled={!siguiente}
          onClick={() => siguiente && onCambiar(siguiente.id)}
        >
          <Icono nombre="der" tamano={20} />
        </button>
      </div>
    </div>
  );
}

function Escena({ adjunto }: { adjunto: Adjunto }) {
  const tipo = tipoDePrevisualizacion(adjunto);
  if (tipo === 'imagen') {
    return (
      <img className="visor__imagen" src={urlAdjunto(adjunto.id, true)} alt={adjunto.nombre} />
    );
  }
  if (tipo === 'texto') return <EscenaTexto adjunto={adjunto} />;
  if (tipo === 'pdf') {
    // Aún no se usa: se habilita en `tipoDePrevisualizacion` cuando la API entregue PDF en línea.
    return (
      <iframe className="visor__pdf" src={urlAdjunto(adjunto.id, true)} title={adjunto.nombre} />
    );
  }
  return (
    <SinVista adjunto={adjunto} mensaje="Este tipo de archivo no se puede previsualizar aquí." />
  );
}

function EscenaTexto({ adjunto }: { adjunto: Adjunto }) {
  const consulta = useTextoAdjunto(adjunto.id, true);
  if (consulta.isPending) return <p className="visor__estado">Cargando el archivo…</p>;
  if (consulta.isError) {
    return (
      <SinVista
        adjunto={adjunto}
        mensaje={`No se pudo mostrar el archivo. ${mensajeDeErrorAdjunto(consulta.error)}`}
      />
    );
  }
  const { texto, truncado } = consulta.data;
  return (
    <div className="visor__texto">
      {truncado && (
        <p className="visor__aviso">
          Mostrando los primeros {Math.round(MAX_BYTES_TEXTO / 1024)} KB. Descarga el archivo para
          verlo completo.
        </p>
      )}
      {/* React lo pone como texto (textContent): un .txt con HTML se ve como texto, no se interpreta. */}
      <pre className="visor__pre" tabIndex={0} aria-label={`Contenido de ${adjunto.nombre}`}>
        {texto}
      </pre>
    </div>
  );
}

function SinVista({ adjunto, mensaje }: { adjunto: Adjunto; mensaje: string }) {
  return (
    <div className="visor__sin-vista">
      <IconoExtension adjunto={adjunto} grande />
      <strong className="visor__sin-vista-nombre">{adjunto.nombre}</strong>
      <p>{mensaje} Descárgalo para abrirlo en tu equipo.</p>
      <a className="boton boton--primario" href={urlAdjunto(adjunto.id)} download={adjunto.nombre}>
        <Icono nombre="bajar" tamano={16} />
        Descargar
      </a>
    </div>
  );
}
